import { Injectable } from '@angular/core';
import { PropertyStateService } from '../state/property-state.service';
import { CalculatorService } from './calculator.service';
import { OfferFlipCalculatorService } from './offer-flip-calculator.service';

@Injectable()
export class AAFlipService {

    constructor(
        private state: PropertyStateService,
        private calculator: CalculatorService,
        private offerFlipCalculator: OfferFlipCalculatorService,
    ) {}

    init() {
        this.calculator.setFieldValuesFromParseObject([this.state.aaFlip_financeBox], this.state.aaFlip);
    }

    calculate() {
        this.updateTopBoxValues();
        this.updateCashBoxValues();
        this.updateFinanceBoxValues();
    }

    updateTopBoxValues() {
        const state = this.state;

        this.calculator.getField(state.aaFlip_sharedTopBox, 'purchase-price').outputValue = this.offerFlipCalculator.offerPrice;
        this.calculator.getField(state.aaFlip_sharedTopBox, 'commission').outputValue = state.offerFlip.get('commission');
        this.calculator.getField(state.aaFlip_sharedTopBox, 'hold-time').outputValue = state.offerFlip.get('holdTime');
        this.calculator.getField(state.aaFlip_sharedTopBox, 'arv').outputValue = state.offerFlip.get('arv');
    }

    updateCashBoxValues() {
        const state = this.state;

        this.calculator.getField(state.aaFlip_cashBox, 'simo-cc').outputValue = this.calculateSimoCC_AAFlip();
        this.calculator.getField(state.aaFlip_cashBox, 'reno-cost').outputValue = state.offerFlip.get('renoCost');
        this.calculator.getField(state.aaFlip_cashBox, 'acqui-cost').outputValue = this.calculateAcquiCost_AAFlip();

        this.calculator.getField(state.aaFlip_cashBox, 'second-closing').outputValue = this.offerFlipCalculator.lessClosingCosts;
        this.calculator.getField(state.aaFlip_cashBox, 'less-commission').outputValue = this.offerFlipCalculator.lessCommission;
        this.calculator.getField(state.aaFlip_cashBox, 'dispo').outputValue = this.calculateDispo_AAFlip();
        this.calculator.getField(state.aaFlip_cashBox, 'less-insurance').outputValue = this.calculateLessInsurance_AAFlip();
        this.calculator.getField(state.aaFlip_cashBox, 'less-utils').outputValue = this.calculateLessUtils_AAFlip();
        this.calculator.getField(state.aaFlip_cashBox, 'taxes').outputValue = state.offerFlip.get('propertyTaxes');
        this.calculator.getField(state.aaFlip_cashBox, 'taxes-while-held').outputValue = this.calculatePropTaxWhileHeld_AAFlip();
        this.calculator.getField(state.aaFlip_cashBox, 'misc').outputValue = this.calculateMisc_AAFlip();
        this.calculator.getField(state.aaFlip_cashBox, 'holding').outputValue = this.calculateHoldingCost_AAFlip();
        this.calculator.getField(state.aaFlip_cashBox, 'total').outputValue = this.calculateTotalExpenses_AAFlip();
        this.calculator.getField(state.aaFlip_cashBox, 'net-income').outputValue = this.calculateNetIncome_AAFlip();
        this.calculator.getField(state.aaFlip_cashBox, 'roi').outputValue = this.calculateROI_AAFlip();
    }

    updateFinanceBoxValues() {
        const state = this.state;

        this.calculator.getField(state.aaFlip_financeBox, 'down-payment').outputValue = this.calculateDownPayment_AAFlip();
        this.calculator.getField(state.aaFlip_financeBox, 'amount-financed').outputValue = this.calculateAmountFinanced_AAFlip();
        this.calculator.getField(state.aaFlip_financeBox, 'annual-loan-payment').outputValue = this.calculateAnnualLoanPayment_AAFlip();
        this.calculator.getField(state.aaFlip_financeBox, 'points-private').outputValue = this.calculateLoanPointsPrivate_AAFlip();
        this.calculator.getField(state.aaFlip_financeBox, 'monthly-loan-payment').outputValue = this.calculateMonthlyLoanPayment_AAFlip();
        this.calculator.getField(state.aaFlip_financeBox, 'total-payment').outputValue = this.calculateTotalPayment_AAFlip();
        this.calculator.getField(state.aaFlip_financeBox, 'holding-plus-loan').outputValue = this.calculateHoldingPlusLoan_AAFlip();
        this.calculator.getField(state.aaFlip_financeBox, 'total-cash').outputValue = this.calculateTotalCashOutOfPocket_AAFlip();
        this.calculator.getField(state.aaFlip_financeBox, 'net-income-after-debt').outputValue = this.calculateNetIncomeAfterDebt_AAFlip();
        this.calculator.getField(state.aaFlip_financeBox, 'cash-on-cash').outputValue = this.calculateCashOnCash_AAFlip();
    }

    calculateNetIncome_AAFlip(): number {
        return this.state.offerFlip.get('arv') - this.calculateTotalExpenses_AAFlip();
    }

    calculateSimoCC_AAFlip(): number {
        return this.offerFlipCalculator.offerPrice * this.state.offerFlip.get('simoCc');
    }

    calculateAcquiCost_AAFlip(): number {
        const purchasePrice = this.calculator.getField(this.state.aaFlip_sharedTopBox, 'purchase-price').outputValue as number;
        const simoCost      = this.calculateSimoCC_AAFlip();
        const renoCost      = this.state.offerFlip.get('renoCost');

        return purchasePrice + simoCost + renoCost;
    }

    calculateDispo_AAFlip(): number {
        return this.offerFlipCalculator.lessClosingCosts + this.offerFlipCalculator.lessCommission;
    }

    calculateLessInsurance_AAFlip(): number {
        const monthlyInsuranceEstimate = this.state.offerFlip.get('arv') * 0.00035;
        return monthlyInsuranceEstimate * this.state.offerFlip.get('holdTime');
    }

    calculateLessUtils_AAFlip(): number {
        return 250 * this.state.offerFlip.get('holdTime');
    }

    calculatePropTaxWhileHeld_AAFlip(): number {

        const annualTaxes = this.state.offerFlip.get('propertyTaxes');
        const holdTime = this.state.offerFlip.get('holdTime');

        return (annualTaxes / 12) * holdTime;
    }

    calculateMisc_AAFlip(): number {
        return 0.08 * this.state.offerFlip.get('renoCost');
    }

    calculateHoldingCost_AAFlip(): number {
        const lessInsurance = this.calculateLessInsurance_AAFlip();
        const lessUtils = this.calculateLessUtils_AAFlip();
        const propTaxWhileHeld = this.calculatePropTaxWhileHeld_AAFlip();
        const misc = this.calculateMisc_AAFlip();

        return lessInsurance + lessUtils + propTaxWhileHeld + misc;
    }

    calculateTotalExpenses_AAFlip(): number {
        const totalAcqui = this.calculateAcquiCost_AAFlip();
        const dispo = this.calculateDispo_AAFlip();
        const holding = this.calculateHoldingCost_AAFlip();

        return totalAcqui + dispo + holding;
    }

    calculateROI_AAFlip(): number {
        const income = this.calculateNetIncome_AAFlip();
        const expenses = this.calculateTotalExpenses_AAFlip();

        return income / expenses;
    }

    calculateDownPayment_AAFlip() {
        // Require these values to be set for calculation
        if (
            this.calculator.isInputUnset(this.calculator.getField(this.state.aaFlip_financeBox, 'down-payment').inputValue) ||
            this.calculator.isInputUnset(this.offerFlipCalculator.offerPrice)
        ) {
            return null;
        }

        return (this.calculator.getField(this.state.aaFlip_financeBox, 'down-payment').inputValue as number) * this.offerFlipCalculator.offerPrice;
    }

    calculateAmountFinanced_AAFlip() {
        // Require these values to be set for calculation
        if (
            this.calculator.isInputUnset(this.calculator.getField(this.state.aaFlip_financeBox, 'down-payment').inputValue) ||
            this.calculator.isInputUnset(this.offerFlipCalculator.offerPrice)
        ) {
            return null;
        }

        return (1 - (this.calculator.getField(this.state.aaFlip_financeBox, 'down-payment').inputValue as number)) * this.offerFlipCalculator.offerPrice;
    }

    calculateAnnualLoanPayment_AAFlip() {
        const amountFinanced = this.calculateAmountFinanced_AAFlip() as number;
        const loanRate = this.calculator.getField(this.state.aaFlip_financeBox, 'loan-rate').inputValue as number;

        // Require these values to be set for calculation
        if (this.calculator.isInputUnset(amountFinanced) || this.calculator.isInputUnset(loanRate)) {
            return null;
        }

        return amountFinanced * loanRate;
    }

    calculateLoanPointsPrivate_AAFlip() {
        const amountFinanced = this.calculateAmountFinanced_AAFlip() as number;
        const points = this.calculator.getField(this.state.aaFlip_financeBox, 'points').inputValue as number;

        // Require these values to be set for calculation
        if (this.calculator.isInputUnset(amountFinanced) || this.calculator.isInputUnset(points)) {
            return null;
        }

        return amountFinanced * points;
    }

    calculateMonthlyLoanPayment_AAFlip() {
        const loanPayment = this.calculateAnnualLoanPayment_AAFlip() as number;

        // Require these values to be set for calculation
        if (this.calculator.isInputUnset(loanPayment)) {
            return null;
        }

        return loanPayment / 12;
    }

    calculateTotalPayment_AAFlip() {
        const monthly = this.calculateMonthlyLoanPayment_AAFlip() as number;
        const holdTime = this.state.offerFlip.get('holdTime');

        // Require these values to be set for calculation
        if (this.calculator.isInputUnset(monthly) || this.calculator.isInputUnset(holdTime)) {
            return null;
        }

        return monthly * holdTime;
    }

    calculateHoldingPlusLoan_AAFlip() {
        const holdingCost = this.calculateHoldingCost_AAFlip();
        const totalLoanPayments = this.calculateTotalPayment_AAFlip() as number;
        const loanPointsPrivate = this.calculateLoanPointsPrivate_AAFlip() as number;

        // Require these values to be set for calculation
        if (this.calculator.isInputUnset(holdingCost) || this.calculator.isInputUnset(totalLoanPayments) || this.calculator.isInputUnset(loanPointsPrivate)) {
            return null;
        }

        return holdingCost + totalLoanPayments + loanPointsPrivate;
    }

    calculateTotalCashOutOfPocket_AAFlip() {
        const renoCost = this.state.offerFlip.get('renoCost');
        const downPayment = this.calculateDownPayment_AAFlip();
        const holdingAndLoan = this.calculateHoldingPlusLoan_AAFlip();


        // Require these values to be set for calculation
        if (this.calculator.isInputUnset(renoCost) || this.calculator.isInputUnset(downPayment) || this.calculator.isInputUnset(holdingAndLoan)) {
            return null;
        }

        return renoCost + downPayment + holdingAndLoan;
    }

    calculateNetIncomeAfterDebt_AAFlip() {
        const arv = this.state.offerFlip.get('arv');
        const acqui = this.calculateAcquiCost_AAFlip();
        const dispo = this.calculateDispo_AAFlip();
        const holdingAndLoan = this.calculateHoldingPlusLoan_AAFlip() as number;

        // Require these values to be set for calculation
        if (
            this.calculator.isInputUnset(arv) ||
            this.calculator.isInputUnset(acqui) ||
            this.calculator.isInputUnset(dispo) ||
            this.calculator.isInputUnset(holdingAndLoan)

        ) {
            return null;
        }

        return arv - acqui - dispo - holdingAndLoan;
    }

    calculateCashOnCash_AAFlip() {
        const net = this.calculateNetIncomeAfterDebt_AAFlip() as number;
        const outOfPockit = this.calculateTotalCashOutOfPocket_AAFlip();


        // Require these values to be set for calculation
        if (this.calculator.isInputUnset(net) || this.calculator.isInputUnset(outOfPockit)) {
            return null;
        }

        return net / outOfPockit;
    }
}
